import { extname } from "path"
import { BYTESCALE_API_PUBLIC_KEY, BYTESCALE_API_SECRET_KEY, ENV } from "../config/env";
import { UploadAPI } from "../api/upload.api";

/**
 * @typedef {Object} GeneratedResourceData
 * @property {string} fileName - Generated unique filename including extension.
 * @property {string} fileNameFallback - Fallback filename (same as fileName by default).
 * @property {boolean} fileNameVariablesEnabled - Whether filename variables are enabled.
 * @property {string} filePath - Full path to the generated file (folderPath + fileName).
 * @property {string} folderPath - Folder path where the file should be stored.
 * @property {boolean} folderPathVariablesEnabled - Whether folder path variables are enabled.
 */

/**
 * FileService provides utilities to upload files and generate resource metadata.
 *
 * - uploadResource: uploads a file using the UploadAPI and returns a public URL (or null on failure)
 * - generateResourceData: creates a unique filename and resource metadata
 * - buildQueryString: builds a URL query string from an object
 *
 * @namespace FileService
 */
export const FileService = {
    /**
     * Builds a FormData object from a file.
     *
     * @param {File|Blob} file - The file object to include in the FormData.
     * @returns {FormData} The constructed FormData object.
     */
    buildFromData: function (file) {
        let formData = new FormData();
        formData.append("file", file);
        return formData;
    },
    /**
     * Uploads a file to the server using the UploadAPI.
     *
     * Behavior:
     * - When running in production (ENV === "production"), a hard-coded public token
     *   is used to upload the file and the URL is read from the returned `data.files[0].fileUrl`.
     * - In non-production environments, the provided `token` argument is used and the
     *   returned URL is expected in `data.url` when `data.status` is truthy.
     *
     * The method returns `null` when the upload fails or the expected response shape
     * is not present.
     *
     * @async
     * @param {File|Blob} file - The file object to upload.
     * @param {string} queryString - Additional query string to append to the upload request.
     * @param {string} [token=""] - Optional authorization token for non-production uploads.
     * @returns {Promise<string|null>} A promise that resolves to the uploaded file URL or null on failure.
    */
    uploadResource: async function (file, queryString, token = "") {
        try {
            const formData = this.buildFromData(file);
            if (ENV === "production") {
                return await this.uploadFileToBytescale(formData, queryString, BYTESCALE_API_PUBLIC_KEY);
            } else {
                return await this.uploadFileToLocalServer(formData, queryString, token);
            }
        } catch (error) {
            return null
        }
    },

    /**
     * Uploads a destination image file to Bytescale.
     *
     * @async
     * @param {File|Blob} file - The image file to upload.
     * @param {string} queryString - Additional query string to append to the upload request.
     * @param {string} [token=""] - Optional authorization token for non-production uploads.
     * @returns {Promise<string|null>} A promise that resolves to the uploaded image URL or null on failure.
    */
    uploadDestinationImage: async function (file, queryString, token = "") {
        try {
            const formData = this.buildFromData(file);
            if (ENV === "production") {
                return await this.uploadFileToBytescale(formData, queryString, BYTESCALE_API_SECRET_KEY);
            } else {
                return await this.uploadFileToLocalServer(formData, queryString, token);
            }
        } catch (error) {
            return null;
        }
    },

    /**
     * Generates resource metadata for a file, including a unique filename.
     *
     * The generated filename has the format `file-<timestamp>-<randomInt><ext>`.
     *
     * @param {string} folderPath - Destination folder path (without trailing slash).
     * @param {string} fileNameWithExt - Original filename including extension.
     * @returns {GeneratedResourceData} Object containing filename, filePath and related flags.
     */
    generateResourceData: function (folderPath, fileNameWithExt) {
        let fileExt = extname(fileNameWithExt);
        let fileName = `file-${Date.now() + '-' + Math.round(Math.random() * 1E9)}${fileExt}`
        return {
            fileName: fileName,
            fileNameFallback: fileName,
            fileNameVariablesEnabled: true,
            filePath: `${folderPath}/${fileName}`,
            folderPath: folderPath,
            folderPathVariablesEnabled: true,
        };
    },

    generateStaticResourceData: function (folderPath, fileName) {
        return {
            fileName: fileName,
            fileNameFallback: fileName,
            fileNameVariablesEnabled: true,
            filePath: `${folderPath}/${fileName}`,
            folderPath: folderPath,
            folderPathVariablesEnabled: true,
        };
    },

    /**
     * Builds a URL-encoded query string from a plain object.
     *
     * Example: { a: 1, b: 'c' } => 'a=1&b=c'
     *
     * @param {Object<string, any>} query - Key/value pairs to convert to a query string.
     * @returns {string} URL-encoded query string.
     */
    buildQueryString: function (query) {
        return new URLSearchParams(query).toString();
    },
    /**
     * Uploads a file to Bytescale using the UploadAPI.
     *
     * @async
     * @param {FormData} formData - The FormData object containing the file to upload.
     * @param {string} queryString - Additional query string to append to the upload request.
     * @param {string} apiKey - The public API key for Bytescale.
     * @returns {Promise<string|undefined>} A promise that resolves to the uploaded file URL or undefined on failure.
    */
    uploadFileToBytescale: async function (formData, queryString, apiKey = "") {
        try {
            const { data } = await UploadAPI.uploadFile(formData, queryString, apiKey);
            if (data.files) {
                let [responseFile] = data.files;
                return responseFile.fileUrl
            }
            return null
        } catch (error) {
            return null
        }
    },
    /**
     * Uploads a file to the local server using the UploadAPI.
     *
     * @async
     * @param {FormData} formData - The FormData object containing the file to upload.
     * @param {string} queryString - Additional query string to append to the upload request.
     * @param {string} [token=""] - Optional authorization token.
     * @returns {Promise<string|null>} A promise that resolves to the uploaded file URL or null on failure.
    */
    uploadFileToLocalServer: async function (formData, queryString, token = "") {
        try {
            const { data } = await UploadAPI.uploadFile(formData, queryString, token);
            if (data.status) {
                return data.url
            }
            return null
        } catch (error) {
            return null
        }
    },
};